import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon } from 'lucide-react';
import { problems } from '../data/problems';
interface PoolProblemsProps {
  onRequestEstimate: (note?: string) => void;
}
export function PoolProblems({
  onRequestEstimate
}: PoolProblemsProps) {
  const [selected, setSelected] = useState<string[]>([]);
  const [activeId, setActiveId] = useState(problems[0].id);
  const active = problems.find(p => p.id === activeId) ?? problems[0];
  const toggle = (id: string) => {
    setActiveId(id);
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const handleRequest = () => {
    const titles = problems.filter(p => selected.includes(p.id)).map(p => p.title.toLowerCase());
    onRequestEstimate(titles.length ? `What I'm noticing: ${titles.join(', ')}.` : undefined);
  };
  return <section className="bg-white py-20 lg:py-28" aria-labelledby="problems-title" data-id="element-186" data-mp="h6767usmzi20">
      <div className="mx-auto grid max-w-7xl gap-10 px-5 lg:grid-cols-12 lg:gap-14 lg:px-8" data-id="element-187" data-mp="1gyzytgo09l4f">
        <div className="lg:col-span-5" data-id="element-188" data-mp="1lwjg6pg02e0l">
          <div className="relative aspect-[4/3] overflow-hidden rounded-2xl bg-navy-900 lg:sticky lg:top-28 lg:aspect-[4/5]" data-id="element-189" data-mp="1lvgr8n28mlnb">
            <AnimatePresence initial={false} data-id="element-190" data-mp="1vn4lswu3cit0">
              <motion.img key={active.image} src={active.image} alt={active.imageAlt} className="absolute inset-0 h-full w-full object-cover" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.25,
              ease: 'easeOut'
            }} data-id="element-191" data-mp="1vlt7i330rsnn" />
            </AnimatePresence>
            <div className="absolute inset-x-0 bottom-0 bg-navy-900/85 px-5 py-4" data-id="element-192" data-mp="1bx104k37ncdt">
              <p className="font-display text-lg font-bold text-white" data-id="element-193" data-mp="2jcffxvgxrey6">{active.title}</p>
              <p className="mt-0.5 text-sm text-white/80" data-id="element-194" data-mp="2ncpj70dcfr9c">{active.description}</p>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7" data-id="element-195" data-mp="obdc62ghw3js">
          <h2 id="problems-title" className="section-title" data-id="element-196" data-mp="jb8sy4o7cupv">
            Does Your Pool Look Like This?
          </h2>
          <p className="mt-4 text-lg text-navy/70" data-id="element-197" data-mp="x8xhbmhhajzo">Tap anything that sounds familiar. We'll add it to your estimate request.</p>

          <ul className="mt-8 grid gap-3 sm:grid-cols-2" data-id="element-198" data-mp="s919fc5nu8eu">
            {problems.map(({
            id,
            title,
            description,
            icon: Icon
          }) => {
            const isOn = selected.includes(id);
            return <li key={id} className="flex" data-id="element-199" data-mp="i4wkbjlo3rk">
                  <button type="button" aria-pressed={isOn} onClick={() => toggle(id)} onMouseEnter={() => setActiveId(id)} onFocus={() => setActiveId(id)} className={`flex w-full items-start gap-4 rounded-2xl border-2 p-5 text-left transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-cyan ${isOn ? 'border-aqua bg-white shadow-[0_8px_24px_-12px_rgba(0,127,175,0.45)]' : 'border-transparent bg-cyan-50 hover:border-accent/50'}`} data-id="element-200" data-mp="eced9blj7m9z">
                    <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-colors duration-150 ${isOn ? 'bg-aqua text-white' : 'bg-white text-aqua'}`} data-id="element-201" data-mp="1oi8p53y8b6vp">
                      {isOn ? <CheckIcon className="h-5 w-5" aria-hidden="true" data-id="element-202" data-mp="228c55d6im4cp" /> : <Icon className="h-5 w-5" aria-hidden="true" data-id="element-203" data-mp="27887z93h7pqo" />}
                    </span>
                    <span data-id="element-204" data-mp="qtykeh72650w">
                      <span className="block font-semibold text-navy" data-id="element-205" data-mp="qtp5pprgrkai">{title}</span>
                      <span className="mt-1 block text-sm leading-relaxed text-navy/65" data-id="element-206" data-mp="vss2kmxaik7e">{description}</span>
                    </span>
                  </button>
                </li>;
          })}
          </ul>

          <div className="mt-10 flex flex-col gap-5 border-t border-line pt-8 md:flex-row md:items-center md:justify-between" data-id="element-207" data-mp="16ax80ef0eim5">
            <p className="max-w-sm text-[15px] leading-relaxed text-navy/75" data-id="element-208" data-mp="12amqc4huu3rg">
              If your pool is showing signs of age, it may be time for a renovation.
            </p>
            <button type="button" onClick={handleRequest} className="btn-primary w-full md:w-auto" data-id="element-209" data-mp="1u09e35f1iak8">
              Request Your Free Estimate
              <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-210" data-mp="1py4n6efz7s1a" />
            </button>
          </div>
        </div>
      </div>
    </section>;
}